import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";

const applications = [
  {
    title: "Construction Industry",
    icon: "🏗️",
    description:
      "Dolomite and calcite used in concrete, mortar, and construction aggregates for structural applications",
  },
  {
    title: "Ceramic & Tiles",
    icon: "🎨",
    description:
      "Premium minerals used in ceramic tiles, sanitaryware, and porcelain manufacturing",
  },
  {
    title: "Glass Manufacturing",
    icon: "🔵",
    description:
      "High-purity minerals essential for producing transparent and colored glass products",
  },
  {
    title: "Chemical Industry",
    icon: "⚗️",
    description:
      "Industrial-grade minerals used in chemical production and processing applications",
  },
  {
    title: "Paint & Coatings",
    icon: "🎭",
    description:
      "Calcite powder used as filler and extender in paints, coatings, and pigments",
  },
  {
    title: "Plastic & Polymers",
    icon: "🔧",
    description:
      "Mineral fillers for improving strength, durability, and thermal properties of plastics",
  },
  {
    title: "Paper & Pulp",
    icon: "📄",
    description:
      "Fine minerals used in paper coating and filling for improved finish and brightness",
  },
  {
    title: "Pharmaceutical",
    icon: "💊",
    description:
      "Ultra-pure minerals meeting pharmaceutical and food-grade standards",
  },
];

export default function Applications() {
  return (
    <Layout>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary to-primary/80 text-white py-16 md:py-24">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">Applications</h1>
          <p className="text-xl text-white/90 max-w-2xl">
            Discover how our mineral products are used across diverse industries
          </p>
        </div>
      </section>

      {/* Applications Grid */}
      <section className="py-20 bg-gradient-to-b from-muted/10 via-white to-muted/20">
        <div className="container mx-auto px-6">
          {/* Applications Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mb-20">
            {applications.map((app, idx) => (
              <div
                key={idx}
                className="group relative bg-white/90 backdrop-blur-sm border border-gray-200 rounded-2xl p-8 shadow-md hover:shadow-xl hover:-translate-y-1 transition-all duration-500 overflow-hidden"
              >
                {/* Glow */}
                <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-accent/10 to-transparent opacity-0 group-hover:opacity-100 blur-xl transition-opacity duration-500"></div>

                {/* Card content */}
                <div className="relative flex items-start gap-5">
                  <div className="text-5xl">{app.icon}</div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-primary mb-3 group-hover:text-accent transition-colors">
                      {app.title}
                    </h3>
                    <p className="text-foreground/70 text-base leading-relaxed group-hover:text-foreground transition-colors">
                      {app.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Info Section */}
          <section className="relative py-20 bg-gradient-to-br from-white via-muted/30 to-primary/5 overflow-hidden">
            <div className="container mx-auto px-6 md:px-10 relative z-10">
              <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl p-10 md:p-16 border border-gray-100 hover:shadow-2xl transition-shadow duration-500">

                {/* Title */}
                <h2 className="text-4xl md:text-5xl font-extrabold text-primary mb-6 relative inline-block">
                  Industry-Specific Solutions
                  <span className="absolute left-0 -bottom-3 w-28 h-[3px] bg-accent rounded-full"></span>
                </h2>

                {/* Description */}
                <p className="text-lg text-foreground/80 mb-6 leading-relaxed">
                  Our minerals are specially processed and graded to meet the specific requirements of each industry. Whether you need ultra-fine particles for paint, coarse aggregates for construction, or chemically pure minerals for pharmaceuticals - we have the solution.
                </p>

                <p className="text-lg text-foreground/80 mb-12 leading-relaxed">
                  Our technical team works closely with customers to understand their unique needs and deliver customized mineral solutions that ensure optimal performance and cost-effectiveness.
                </p>

                {/* Cards Section */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {/* Card 1 */}
                  <div className="group relative bg-gradient-to-br from-primary/10 via-white to-accent/10 p-8 rounded-2xl border border-gray-100 shadow-md hover:shadow-lg transition-all duration-500 hover:-translate-y-1">
                    <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-accent/10 opacity-0 group-hover:opacity-100 blur-2xl transition-opacity duration-700"></div>
                    <div className="relative">
                      <h3 className="text-xl font-semibold text-primary mb-5 flex items-center gap-2">
                        <span className="text-2xl">✅</span> Quality Assurance
                      </h3>
                      <ul className="space-y-2 text-foreground/70 text-base leading-relaxed">
                        <li>✓ ISO certified processing</li>
                        <li>✓ Strict quality control</li>
                        <li>✓ Consistent specifications</li>
                        <li>✓ Regular testing</li>
                      </ul>
                    </div>
                  </div>

                  {/* Card 2 */}
                  <div className="group relative bg-gradient-to-br from-accent/10 via-white to-primary/10 p-8 rounded-2xl border border-gray-100 shadow-md hover:shadow-lg transition-all duration-500 hover:-translate-y-1">
                    <div className="absolute inset-0 bg-gradient-to-br from-accent/10 to-primary/10 opacity-0 group-hover:opacity-100 blur-2xl transition-opacity duration-700"></div>
                    <div className="relative">
                      <h3 className="text-xl font-semibold text-primary mb-5 flex items-center gap-2">
                        <span className="text-2xl">🏅</span> Certifications
                      </h3>
                      <ul className="space-y-2 text-foreground/70 text-base leading-relaxed">
                        <li>✓ ISO 9001:2015</li>
                        <li>✓ Environmental compliance</li>
                        <li>✓ Export standards met</li>
                        <li>✓ Industry certifications</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Subtle background glow */}
            <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-tr from-primary/5 via-transparent to-accent/5 blur-3xl opacity-70"></div>
          </section>

        </div>
      </section>



      {/* CTA Section */}
      <section className="relative py-20 md:py-28 bg-gradient-to-br from-primary via-primary/90 to-primary/80 text-white overflow-hidden">
        {/* Decorative Background Glow */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,0.15),transparent_60%)]"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_right,rgba(255,255,255,0.1),transparent_70%)]"></div>

        <div className="container relative z-10 mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-extrabold mb-6 tracking-tight">
            Need Minerals for Your Industry?
          </h2>

          <p className="text-lg md:text-xl text-white/90 mb-10 max-w-2xl mx-auto leading-relaxed">
            Let’s collaborate to find the perfect mineral solution tailored to your
            industry’s unique needs.
          </p>

          <Link
            to="/contact"
            className="inline-flex items-center gap-3 bg-accent text-accent-foreground px-8 py-4 rounded-xl font-semibold text-lg shadow-lg hover:shadow-accent/40 hover:scale-105 transition-all duration-300"
          >
            Contact Us Today
            <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
          </Link>

          {/* Subtext for added credibility */}
          <p className="mt-6 text-sm text-white/70">
            Trusted by industries worldwide - from construction to pharmaceuticals.
          </p>
        </div>

        {/* Soft Gradient Border Effect */}
        <div className="absolute top-0 left-0 w-full h-full border-t border-white/10"></div>
      </section>

    </Layout>
  );
}
